import type { PageContext } from '@template-project/funnels-contract';

export interface ParamsFromContext {
  value?: unknown;
  blockProps?: Record<string, unknown>;
  pageContext?: Partial<PageContext>;
  eventMeta?: Record<string, unknown>;
}

const TOKEN = /^\$([a-zA-Z0-9_.]+)$/;

function resolveToken(token: string, ctx: ParamsFromContext): unknown {
  const match = TOKEN.exec(token.trim());
  if (!match) return token;

  const path = match[1];
  if (path === 'value') return ctx.value;
  if (path.startsWith('block.props.')) {
    const key = path.slice('block.props.'.length);
    return ctx.blockProps?.[key];
  }
  if (path.startsWith('cart.')) {
    const field = path.slice('cart.'.length);
    const map: Record<string, unknown> = {
      total: ctx.pageContext?.cartTotal,
      itemCount: ctx.pageContext?.cartItemCount,
      productIds: ctx.pageContext?.cartProductIds,
      collections: ctx.pageContext?.cartCollections,
      tags: ctx.pageContext?.cartProductTags,
    };
    return map[field];
  }
  if (path.startsWith('order.')) {
    const field = path.slice('order.'.length);
    const map: Record<string, unknown> = {
      id: ctx.pageContext?.orderId,
      total: ctx.pageContext?.orderTotal,
      productIds: ctx.pageContext?.orderProductIds,
    };
    return map[field];
  }
  if (path.startsWith('product.')) {
    const field = path.slice('product.'.length);
    const map: Record<string, unknown> = {
      id: ctx.pageContext?.productId,
      tags: ctx.pageContext?.productTags,
      collections: ctx.pageContext?.productCollections,
    };
    return map[field];
  }
  if (path.startsWith('session.')) {
    const field = path.slice('session.'.length);
    const map: Record<string, unknown> = {
      count: ctx.pageContext?.sessionCount,
      deviceType: ctx.pageContext?.deviceType,
      country: ctx.pageContext?.country,
      browserLanguage: ctx.pageContext?.browserLanguage,
    };
    return map[field];
  }
  if (path.startsWith('event.')) {
    const field = path.slice('event.'.length);
    return ctx.eventMeta?.[field];
  }
  if (path.startsWith('url.')) {
    const field = path.slice('url.'.length);
    if (field === 'path' && ctx.pageContext?.url) {
      try {
        return new URL(ctx.pageContext.url).pathname;
      } catch {
        return ctx.pageContext.url;
      }
    }
    if (field === 'query' && ctx.pageContext?.urlParameters) {
      return ctx.pageContext.urlParameters;
    }
  }
  return undefined;
}

/** Resolve static params + paramsFrom DSL (unresolved → omitted). */
export function resolveEventParams(
  staticParams: Record<string, unknown> | undefined,
  paramsFrom: Record<string, string> | undefined,
  ctx: ParamsFromContext,
): Record<string, unknown> {
  const out: Record<string, unknown> = { ...(staticParams ?? {}) };
  if (!paramsFrom) return out;

  for (const [key, expr] of Object.entries(paramsFrom)) {
    const resolved = resolveToken(expr, ctx);
    if (resolved !== undefined) {
      out[key] = resolved;
    }
  }
  return out;
}

/**
 * Permissive hook shape accepted by the client evaluator. The canonical
 * `StepHook` is assignable to this: the evaluator tolerates operator aliases
 * (`gte`, `eq`, …) and client field names (`scroll_depth_pct`), so its input is
 * intentionally looser than the strict schema enums.
 */
export interface EvaluableStepHook {
  logic?: string;
  groups?: Array<{
    logic?: string;
    conditions?: Array<{ field: string; operator: string; value: unknown }>;
  }>;
}

/** Client-side StepHook evaluator — parity with Go triggerengine. */
export function evaluateStepHook(
  hook: EvaluableStepHook | undefined,
  pageContext: Partial<PageContext>,
): boolean {
  if (!hook || !hook.groups?.length) return true;

  const groupResults = hook.groups.map((group) => {
    const conditionResults = (group.conditions ?? []).map((cond) => {
      const fieldValue = getContextField(pageContext, cond.field);
      const op = normalizeOperator(cond.operator);
      return compare(fieldValue, op, cond.value, cond.field);
    });
    return group.logic === 'OR'
      ? conditionResults.some(Boolean)
      : conditionResults.every(Boolean);
  });

  return hook.logic === 'OR'
    ? groupResults.some(Boolean)
    : groupResults.every(Boolean);
}

function normalizeOperator(operator: string): string {
  const aliases: Record<string, string> = {
    eq: 'equals',
    ne: 'not_equals',
    gt: 'greater_than',
    gte: 'greater_than_or_equals',
    lt: 'less_than',
    lte: 'less_than_or_equals',
    url_contains: 'contains',
    // Tolerate the legacy singular spelling; the schema taxonomy is plural.
    greater_than_or_equal: 'greater_than_or_equals',
    less_than_or_equal: 'less_than_or_equals',
  };
  return aliases[operator] ?? operator;
}

function getContextField(ctx: Partial<PageContext>, field: string): unknown {
  const map: Record<string, unknown> = {
    current_url: ctx.url,
    url: ctx.url,
    device_type: ctx.deviceType,
    country: ctx.country,
    cart_total: ctx.cartTotal,
    cart_item_count: ctx.cartItemCount,
    scroll_depth: ctx.scrollDepthPct,
    scroll_depth_pct: ctx.scrollDepthPct,
    time_on_page: ctx.timeOnPageSec,
    time_on_page_sec: ctx.timeOnPageSec,
    exit_intent: ctx.exitIntent,
    browser_language: ctx.browserLanguage,
    session_count: ctx.sessionCount,
    page_type: ctx.pageType,
    product_id: ctx.productId,
    collection_id: ctx.collectionId,
    is_returning: ctx.isReturning,
    page_view_count: ctx.pageViewCount,
    day_of_week: ctx.dayOfWeek,
    time_of_day: ctx.timeOfDay,
    landing_url: ctx.landingUrl,
    url_parameters: ctx.urlParameters,
    customer_tags: ctx.customerTags,
  };
  if (field in map) return map[field];
  if (field === 'url_parameter') return ctx.urlParameters;
  if (field === 'cookie') return readCookies();
  return (ctx as Record<string, unknown>)[field];
}

/** Parse document.cookie into a name→value map. SSR/test-safe (returns {}). */
function readCookies(): Record<string, string> {
  if (typeof document === 'undefined' || !document.cookie) return {};
  return document.cookie.split(';').reduce<Record<string, string>>((acc, pair) => {
    const idx = pair.indexOf('=');
    if (idx === -1) return acc;
    const key = pair.slice(0, idx).trim();
    if (key) acc[key] = decodeURIComponent(pair.slice(idx + 1).trim());
    return acc;
  }, {});
}

/**
 * Lowercase a value's string form. String and set comparisons in the Go trigger
 * engine are case-insensitive (`strings.EqualFold` / `strings.ToLower`), so the
 * client evaluator lowercases both operands to stay in parity — otherwise an
 * action hook comparing e.g. country "us" vs "US" would silently disagree with
 * the server.
 */
function lc(value: unknown): string {
  return String(value ?? '').toLowerCase();
}

function compare(
  actual: unknown,
  operator: string,
  expected: unknown,
  field?: string,
): boolean {
  if (field === 'url_parameter' || field === 'cookie') {
    const spec = expected as { key?: string; value?: unknown } | undefined;
    if (!spec?.key) return false;
    const bag = actual as Record<string, string> | undefined;
    const val = bag?.[spec.key];
    if (operator === 'equals') return lc(val) === lc(spec.value);
    if (operator === 'not_equals') return lc(val) !== lc(spec.value);
    if (operator === 'contains') return lc(val).includes(lc(spec.value));
    if (operator === 'not_contains') return !lc(val).includes(lc(spec.value));
    if (operator === 'is_empty') return val == null || val === '';
    if (operator === 'is_not_empty') return val != null && val !== '';
    return false;
  }

  switch (operator) {
    case 'equals':
      return actual === expected || lc(actual) === lc(expected);
    case 'not_equals':
      return actual !== expected && lc(actual) !== lc(expected);
    case 'greater_than':
      return Number(actual) > Number(expected);
    case 'greater_than_or_equal':
    case 'greater_than_or_equals':
      return Number(actual) >= Number(expected);
    case 'less_than':
      return Number(actual) < Number(expected);
    case 'less_than_or_equal':
    case 'less_than_or_equals':
      return Number(actual) <= Number(expected);
    case 'between':
      return (
        Array.isArray(expected) &&
        expected.length === 2 &&
        Number(actual) >= Number(expected[0]) &&
        Number(actual) <= Number(expected[1])
      );
    case 'contains':
      return lc(actual).includes(lc(expected));
    case 'not_contains':
      return !lc(actual).includes(lc(expected));
    case 'in':
    case 'in_list':
      return Array.isArray(expected) && expected.map(lc).includes(lc(actual));
    case 'not_in':
      return Array.isArray(expected) && !expected.map(lc).includes(lc(actual));
    case 'is_empty':
      return actual == null || actual === '' || (Array.isArray(actual) && actual.length === 0);
    case 'is_not_empty':
      return actual != null && actual !== '' && !(Array.isArray(actual) && actual.length === 0);
    case 'starts_with':
      return lc(actual).startsWith(lc(expected));
    case 'ends_with':
      return lc(actual).endsWith(lc(expected));
    default:
      return false;
  }
}
